import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useApiRequest from '../lib/hooks/useRequest';
import { CompleteLessonResponse, CourseDetailsUser, CourseLessonDetails, CourseLessonUserTreeNode } from '../lib/interfaces/courses';

/** A lesson flattened out of the tree, carrying its module title for the breadcrumb. */
export interface FlatLesson {
  moduleTitle: string;
  lesson: CourseLessonUserTreeNode;
}

/** 1 = moving forward (swipe up to the next), -1 = moving back. Drives the transition direction. */
export type LessonDirection = 1 | -1;

export interface UseCourseDetail {
  course: CourseDetailsUser | null;
  isLoadingCourse: boolean;
  /** The flat, ordered lessons — modules in order, lessons in each module's order. */
  flatLessons: FlatLesson[];
  currentLessonId: string | null;
  /** Breadcrumb data for the current lesson (module + lesson titles). */
  currentEntry: FlatLesson | null;
  /** Full content of the current lesson, or null while it loads or when its fetch failed. */
  currentContent: CourseLessonDetails | null;
  /** The current lesson's content failed to load — the player offers a retry. Null content without
   *  this flag means it is still loading. */
  hasContentError: boolean;
  /** Retries loading the current lesson's content after a failure. */
  retryContent: () => void;
  direction: LessonDirection;
  isCompleting: boolean;
  /** True when the current lesson is the last in the course — the button then completes the course. */
  isLastLesson: boolean;
  goToLesson: (lessonId: string) => void;
  /** Marks the current lesson complete and advances to the next (or finishes the course). */
  completeCurrent: () => Promise<CompleteLessonResponse | null>;
}

/** Sets a lesson's `isCompleted` and the course percentage without refetching the whole tree. */
const withLessonCompleted = (course: CourseDetailsUser, lessonId: string, percentage: number): CourseDetailsUser => ({
  ...course,
  percentage,
  modules: course.modules.map((module) => ({
    ...module,
    lessons: module.lessons.map((lesson) => (lesson._id === lessonId ? { ...lesson, isCompleted: true } : lesson))
  }))
});

/**
 * Loads a course for the learner and drives lesson navigation.
 *
 * The course tree is fetched once; heavy lesson content is fetched per lesson and cached, with the
 * next lesson prefetched so completing one swipes to a lesson that is already there. Completion,
 * percentage and the resume position are all reflected locally, so nothing refetches on a click.
 */
const useCourseDetail = (courseId: string): UseCourseDetail => {
  const { apiRequest } = useApiRequest();

  const [course, setCourse] = useState<CourseDetailsUser | null>(null);
  const [isLoadingCourse, setIsLoadingCourse] = useState(true);
  const [currentLessonId, setCurrentLessonId] = useState<string | null>(null);
  const [direction, setDirection] = useState<LessonDirection>(1);
  const [isCompleting, setIsCompleting] = useState(false);
  const [contentCache, setContentCache] = useState<Record<string, CourseLessonDetails>>({});
  // Lessons whose content fetch failed. Kept separate from the cache so a failure shows an error
  // state instead of an endless skeleton, and a retry can clear it.
  const [contentErrors, setContentErrors] = useState<Record<string, true>>({});

  const flatLessons = useMemo<FlatLesson[]>(
    () => course?.modules.flatMap((module) => module.lessons.map((lesson) => ({ moduleTitle: module.title, lesson }))) ?? [],
    [course]
  );

  // Read inside stable callbacks that must not close over stale values.
  const orderedIdsRef = useRef<string[]>([]);
  orderedIdsRef.current = flatLessons.map((entry) => entry.lesson._id);
  const currentIdRef = useRef<string | null>(null);
  currentIdRef.current = currentLessonId;

  const loadCourse = useCallback(async () => {
    setIsLoadingCourse(true);
    try {
      const res = await apiRequest<CourseDetailsUser>(`courses/user/${courseId}`, 'GET');
      if (res.data) {
        setCourse(res.data);
        // Keep the reader's place on a refetch (e.g. token refresh); only the first load resumes.
        setCurrentLessonId((prev) => prev ?? res.data!.resumeLessonId);
      }
    } finally {
      setIsLoadingCourse(false);
    }
  }, [apiRequest, courseId]);

  useEffect(() => {
    loadCourse();
  }, [loadCourse]);

  // In-flight lesson fetches, so a lesson and its prefetched neighbour are never requested twice.
  const inFlightRef = useRef<Set<string>>(new Set());
  // The cache is read through a ref, not a dependency: depending on `contentCache` would give
  // loadLesson a new identity on every store, re-firing the prefetch effect after each load and
  // spraying redundant requests. The ref keeps loadLesson stable while still seeing the latest cache.
  const contentCacheRef = useRef(contentCache);
  contentCacheRef.current = contentCache;

  const loadLesson = useCallback(
    async (lessonId: string) => {
      if (contentCacheRef.current[lessonId] || inFlightRef.current.has(lessonId)) return;
      inFlightRef.current.add(lessonId);
      try {
        const res = await apiRequest<CourseLessonDetails>(`course-lessons/user/${lessonId}`, 'GET');
        if (res.data) {
          setContentCache((prev) => ({ ...prev, [lessonId]: res.data! }));
          // Clear a prior error so a successful retry drops the error state.
          setContentErrors((prev) => {
            if (!prev[lessonId]) return prev;
            const next = { ...prev };
            delete next[lessonId];
            return next;
          });
        } else {
          setContentErrors((prev) => ({ ...prev, [lessonId]: true }));
        }
      } finally {
        inFlightRef.current.delete(lessonId);
      }
    },
    [apiRequest]
  );

  // Clears the error and re-fetches. loadLesson's guards let it through since a failed lesson is
  // neither cached nor in flight.
  const retryContent = useCallback(() => {
    const lessonId = currentIdRef.current;
    if (!lessonId) return;
    setContentErrors((prev) => {
      if (!prev[lessonId]) return prev;
      const next = { ...prev };
      delete next[lessonId];
      return next;
    });
    loadLesson(lessonId);
  }, [loadLesson]);

  // Load the current lesson, then prefetch the next so the forward swipe lands on ready content.
  useEffect(() => {
    if (!currentLessonId) return;
    loadLesson(currentLessonId);

    // Guard the -1 case: an unknown current lesson must not prefetch ids[0] as if it were the next.
    const ids = orderedIdsRef.current;
    const idx = ids.indexOf(currentLessonId);
    const nextId = idx === -1 ? undefined : ids[idx + 1];
    if (nextId) loadLesson(nextId);
  }, [currentLessonId, loadLesson]);

  const goToLesson = useCallback((lessonId: string) => {
    const ids = orderedIdsRef.current;
    const from = ids.indexOf(currentIdRef.current ?? '');
    const to = ids.indexOf(lessonId);
    setDirection(to >= from ? 1 : -1);
    setCurrentLessonId(lessonId);
  }, []);

  const completeCurrent = useCallback(async (): Promise<CompleteLessonResponse | null> => {
    const lessonId = currentIdRef.current;
    if (!lessonId || !course) return null;

    setIsCompleting(true);
    try {
      const res = await apiRequest<CompleteLessonResponse>(`courses/user/${course._id}/lessons/${lessonId}/complete`, 'PATCH');
      if (!res.data) return null;

      setCourse((prev) => (prev ? withLessonCompleted(prev, lessonId, res.data!.percentage) : prev));
      if (res.data.nextLessonId) goToLesson(res.data.nextLessonId);
      return res.data;
    } finally {
      setIsCompleting(false);
    }
  }, [apiRequest, course, goToLesson]);

  // Cheap derivations off flatLessons — plain computation, no memo needed, and no ref so the lint
  // dependency is honest.
  const currentEntry = flatLessons.find((entry) => entry.lesson._id === currentLessonId) ?? null;
  const lastLesson = flatLessons[flatLessons.length - 1];
  const isLastLesson = !!currentLessonId && lastLesson?.lesson._id === currentLessonId;

  const currentContent = currentLessonId ? (contentCache[currentLessonId] ?? null) : null;
  const hasContentError = !!currentLessonId && !currentContent && !!contentErrors[currentLessonId];

  return {
    course,
    isLoadingCourse,
    flatLessons,
    currentLessonId,
    currentEntry,
    currentContent,
    hasContentError,
    retryContent,
    direction,
    isCompleting,
    isLastLesson,
    goToLesson,
    completeCurrent
  };
};

export default useCourseDetail;
